import { Component, OnInit, Inject } from "@angular/core"
import { MAT_DIALOG_DATA, MatDialogRef } from "@angular/material/dialog"
import { CommunicationApiService } from "@api/communication/communication-api.service"
import { ParentNode } from "@api/communication/models/node"
import { ToastServiceV2 } from "@shared/services/toast-v2/toast-v2.service"
import { LabelValue } from "app/communication/messages/edit-template-view/edit-email/edit-email-lists"
import html2canvas from "html2canvas"
import { jsPDF } from "jspdf"

@Component({
  selector: "app-confirm-download-dialog",
  templateUrl: "./confirm-download-dialog.component.html",
  styleUrls: ["./confirm-download-dialog.component.scss"],
  standalone: false,
})
export class ConfirmDownloadDialogComponent implements OnInit {
  public dialogTitle: string
  public dialogDescription: string
  public previewMode: boolean = false
  public previewCanvas: HTMLCanvasElement | null = null
  public loading: boolean = false
  public languageList: LabelValue[] = []
  public currentLanguage: LabelValue = { label: "", value: "" }
  private isDraft: boolean = true
  public isPublished: boolean = false

  constructor(
    @Inject(MAT_DIALOG_DATA) public data: any,
    public dialogRef: MatDialogRef<ConfirmDownloadDialogComponent>,
    private communicationService: CommunicationApiService,
  ) {}

  ngOnInit(): void {
    this.dialogTitle = this.data.dialogTitle
    this.dialogDescription = this.data.dialogDescription
    this.data.languageList.forEach((x) => {
      if (x.code.includes("default")) {
        this.currentLanguage = { label: x.name, value: x.code }
      }
      this.languageList.push({ label: x.name, value: x.code })
    })
    this.isPublished = this.data.isPublished
  }

  public changeVersion(version: string): void {
    this.isDraft = version === "Draft" ? true : false
  }

  seePreview() {
    this.loading = true
    this.previewMode = true
    this.communicationService.Template.GetHtmlPreview(this.data.hotelId, this.data.templateId, this.currentLanguage.value, this.isDraft).subscribe(
      (html) => {
        const tempElement = document.createElement("div")
        tempElement.innerHTML = html

        const tables = tempElement.querySelectorAll("table")
        tables.forEach((table) => {
          table.style.borderCollapse = "collapse"
          table.style.marginTop = "-1px" //Margin adjustments to prevent gaps
          table.style.marginBottom = "-1px"
          const cells = table.querySelectorAll("td")
          cells.forEach((cell) => {
            cell.style.marginTop = "-1px"
            cell.style.marginBottom = "-1px"
          })
        })

        const links = tempElement.querySelectorAll("a")
        links.forEach((link) => {
          link.style.textDecoration = "underline"
        })

        tempElement.style.width = "794px"
        tempElement.style.position = "absolute"
        tempElement.style.top = "-9999px"
        document.body.appendChild(tempElement)
        // Wait for images to load
        const images = tempElement.querySelectorAll("img")
        const imagePromises: Promise<void>[] = []

        images.forEach((img) => {
          imagePromises.push(
            new Promise((resolve, reject) => {
              img.onload = () => resolve()
              img.onerror = () => {
                console.error("Failed to load image:", img.src)
                resolve() // Resolve even on error to continue PDF generation
              }
            }),
          )
        })
        // const previewContainer = document.getElementById("canvas-preview-container")
        // if (previewContainer) {
        //   previewContainer.innerHTML = html // Clear previous content
        //   this.loading = false
        // }

        Promise.all(imagePromises).then(() => {
          // Get the total height of the content
          const fullHeight = tempElement.scrollHeight

          // Create a jsPDF instance with a custom height
          let pdf = new jsPDF("p", "mm", [210, fullHeight * 0.264583], true) // A4 width, height in mm

          html2canvas(tempElement, {
            width: 794,
            height: fullHeight,
            windowWidth: 794,
            windowHeight: fullHeight,
            scale: 5,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: null,
            removeContainer: true,
          }).then((canvas) => {
            document.body.removeChild(tempElement)
            const contentDataURL = canvas.toDataURL("image/png")
            const imgWidth = pdf.internal.pageSize.getWidth() // Convert px to mm (1px = 0.264583mm)
            const imgHeight = (canvas.height * imgWidth) / canvas.width
            const previewContainer = document.getElementById("canvas-preview-container")
            if (previewContainer) {
              previewContainer.innerHTML = "" // Clear previous content
              this.previewCanvas = canvas
              const imgElement = document.createElement("img")
              imgElement.src = contentDataURL
              imgElement.style.width = "100%" // Adjust width to fit container
              previewContainer.appendChild(imgElement)
            }

            this.loading = false
          })
        })
      },
      (error) => {
        this.loading = false
      },
    )
  }

  submitQuickAction() {
    this.loading = true
    this.communicationService.Template.GetHtmlPreview(this.data.hotelId, this.data.templateId, this.currentLanguage.value, this.isDraft).subscribe(
      (html) => {
        const tempElement = document.createElement("div")
        tempElement.innerHTML = html

        const tables = tempElement.querySelectorAll("table")
        tables.forEach((table) => {
          table.style.borderCollapse = "collapse"
          table.style.marginTop = "-1px" // Margin adjustments to prevent gaps
          table.style.marginBottom = "-1px"
          const cells = table.querySelectorAll("td")
          cells.forEach((cell) => {
            cell.style.marginTop = "-1px"
            cell.style.marginBottom = "-1px"
          })
        })

        const links = tempElement.querySelectorAll("a")
        links.forEach((link) => {
          link.style.textDecoration = "underline"
        })

        tempElement.style.width = "794px"
        tempElement.style.position = "absolute"
        tempElement.style.top = "-9999px"
        document.body.appendChild(tempElement)

        // Wait for images to load
        const images = tempElement.querySelectorAll("img")
        const imagePromises: Promise<void>[] = []

        images.forEach((img) => {
          imagePromises.push(
            new Promise((resolve, reject) => {
              img.onload = () => resolve()
              img.onerror = () => {
                console.error("Failed to load image:", img.src)
                resolve() // Resolve even on error to continue PDF generation
              }
            }),
          )
        })

        Promise.all(imagePromises).then(() => {
          // Get the total height of the content
          const fullHeight = tempElement.scrollHeight

          html2canvas(tempElement, {
            width: 794,
            height: fullHeight,
            windowWidth: 794,
            windowHeight: fullHeight,
            scale: 2,
            useCORS: true,
            allowTaint: true,
            backgroundColor: "#ffffff",
          }).then((canvas) => {
            // Create a jsPDF instance with a custom height
            let pdf = new jsPDF("p", "px", [canvas.width, canvas.height], true)

            document.body.removeChild(tempElement)
            const contentDataURL = canvas.toDataURL("image/jpeg", 0.95)
            const imgWidth = pdf.internal.pageSize.getWidth() // Convert px to mm (1px = 0.264583mm)
            const imgHeight = (canvas.height * imgWidth) / canvas.width
            pdf.addImage(contentDataURL, "JPEG", 0, 0, imgWidth, imgHeight)
            pdf.viewerPreferences({ FitWindow: true }, true)
            pdf.save(`${this.data.templateName}.pdf`)
            this.loading = false
            this.dialogRef.close({
              success: true,
            })
          })
        })
      },
      (error) => {
        this.loading = false
      },
    )
  }
  closeDialog() {
    this.dialogRef.close({
      success: false,
    })
  }
}
